import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface CarouselProps {
  children: React.ReactNode;
  className?: string;
  /** Gap utility between items */
  gap?: string;
  /** Accessible name for the scroll region */
  label?: string;
}

/**
 * Carousel — the shared horizontally scrolling row with hover arrows.
 *
 * Items are passed as children; arrow styling comes from the active brand's
 * surface tokens, so every row looks native on both platforms.
 */
export const Carousel: React.FC<CarouselProps> = ({
  children,
  className = '',
  gap = 'gap-4',
  label
}) => {
  const dk = useDarkClasses();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const checkScroll = () => {
    if (scrollRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth
      } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      // Small buffer (2px) absorbs sub-pixel rounding
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 2);
    }
  };
  useEffect(() => {
    checkScroll();
    // Re-check once images have had a moment to size the row
    const timeoutId = setTimeout(checkScroll, 100);
    window.addEventListener('resize', checkScroll);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScroll);
    };
  }, [children]);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.8;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const arrowClass = `absolute top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md border ${dk.carouselArrow} hidden md:flex items-center justify-center p-2 opacity-0 group-hover/scroll:opacity-100 focus-visible:opacity-100 transition-opacity duration-200`;
  return <div className={`relative group/scroll ${className}`} role="region" aria-label={label} data-id="element-3404" data-mp="3dkanz592o3bj">
      <div ref={scrollRef} onScroll={checkScroll} className={`flex overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory ${gap}`} style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-3405" data-mp="38mhgm8w1939o">
        {children}
      </div>

      {canScrollLeft && <button type="button" onClick={() => scroll('left')} className={`${arrowClass} left-0 -translate-x-4`} aria-label="Scroll left" data-id="element-3406" data-mp="6pio59e7lui">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-3407" data-mp="3nb5ckg9sjf05" />
        </button>}

      {canScrollRight && <button type="button" onClick={() => scroll('right')} className={`${arrowClass} right-0 translate-x-4`} aria-label="Scroll right" data-id="element-3408" data-mp="2gujtv74p0i69">
          <ChevronRightIcon className="h-5 w-5" data-id="element-3409" data-mp="rdoa3e01a6fl" />
        </button>}
    </div>;
};
export default Carousel;